import type { WhitelistItem } from "@gearbox-protocol/cli-utils";
import {
  AddressMap,
  json_parse,
  MultichainSDK,
  type MultichainState,
  type NetworkType,
  type TokenMetaData,
} from "@gearbox-protocol/sdk/onchain";
import { AccountsPlugin } from "@gearbox-protocol/sdk/plugins/accounts";
import { STATE_CACHE_URL_BASE } from "api";
import { useAsync } from "react-use";
import type { AsyncState } from "react-use/lib/useAsyncFn";
import type { Address } from "viem";

export interface NetAccount {
  address: Address;
  network: NetworkType;
  underlying?: TokenMetaData;
  debt?: bigint;
  reason: string;
}

/**
 * Returns list of accounts in whitelist that are not open
 * @param whitelist
 * @returns
 */
export default function useAllClosedAccounts(
  whitelist: WhitelistItem[],
): AsyncState<NetAccount[]> {
  return useAsync(async () => {
    const response = await fetch(`${STATE_CACHE_URL_BASE}/multichain.json`);
    const txt = await response.text();
    const state = json_parse(txt) as MultichainState<{
      readonly accounts: AccountsPlugin;
    }>;

    const chains = Object.fromEntries(
      state.chains.map(c => [c.network, { rpcURLs: ["http://null.local"] }]),
    ) as Record<NetworkType, { rpcURLs: string[] }>;

    const sdk = new MultichainSDK<{ readonly accounts: AccountsPlugin }>({
      chains,
      plugins: { accounts: () => new AccountsPlugin() },
    });
    sdk.hydrate(state);

    const openAccounts = new Map<NetworkType, AddressMap<NetAccount>>();
    for (const [network, chainSdk] of sdk.chains) {
      openAccounts.set(
        network,
        new AddressMap(
          chainSdk.plugins.accounts.accounts.map(a => [
            a.creditAccount,
            {
              address: a.creditAccount,
              network,
              debt: a.debt,
              underlying: chainSdk.tokensMeta.mustGet(a.underlying),
              reason: "",
            },
          ]),
        ),
      );
    }

    return whitelist
      .map(item => {
        if (item.contractType !== "CREDIT_ACCOUNT") {
          return undefined;
        }
        const ca = openAccounts.get(item.network)?.get(item.address);
        return ca?.debt
          ? undefined
          : {
              ...ca,
              network: item.network,
              address: item.address,
              reason: item.reason,
            };
      })
      .filter(Boolean) as NetAccount[];
  }, [whitelist]);
}
